import { UniversalNodeRegistry } from '@/features/backtest/schemas/universal-node-schema'
import type { UniversalNodeSchema } from '@/features/backtest/schemas/universal-node-schema'
import { getParameterValue } from '@/features/backtest/components/nodes/universal-node-helpers'

export interface ConnectionValidationResult {
  isValid: boolean
  errorMessage?: string
}

/*
 * The one set of connection rules (audit 2026-10-06, step 3). The canvas, the
 * graph validator (AI batches, readiness, the status chip), drop and splice,
 * bypass wires and graph-ops' input picker all ask validateNodeConnection, so
 * a wire the user cannot draw is one the AI cannot build, and the other way
 * round. graph-engine/type-compatibility-invariants.unit.test.ts holds every
 * registered output against every declared input through both layers.
 */

// Type compatibility matrix for connection validation. `ohlcv` into a number
// input needs the input to pick a field (isTypeCompatible).
const connectionCompatibilityMatrix: Record<string, string[]> = {
  ohlcv: ['ohlcv', 'number'],
  number: ['number'], // Numbers can only connect to numbers
  boolean: ['boolean'], // Booleans can only connect to booleans
  float: ['number', 'float'],
  int: ['number', 'int'],
}

// Source-category aliases: an input's acceptedSources may list a node type,
// a category, or one of these legacy aliases.
// Keyed by the SOURCE's category: a 'filter' node satisfies an input that
// lists 'filters' (the alias used to run the other way, so no filter node
// could match).
const sourceCategoryAliases: Record<string, string[]> = {
  data: ['price-data', 'market-data'],
  indicators: ['indicator'],
  utilities: ['utility'],
  filter: ['filters'],
}

/**
 * Whether `sourceSchema`'s category satisfies an input's `acceptedSources`.
 *
 * Passthrough-output nodes (Bar Offset — `output.passthrough: true`) are
 * CATEGORY-TRANSPARENT: they forward whatever value flows into them unchanged
 * (just shifted in time), so the node's OWN category must not gate the
 * connection — only type compatibility should. Without this, a boolean routed
 * from a logic node through Bar Offset (category 'utilities') was rejected by
 * boolean-logic inputs that allowlist only ['logic'] (e.g. Signal Hold). The
 * caller still enforces type compatibility separately, so a Bar Offset
 * carrying a number cannot satisfy a boolean-only input on the type check.
 */
function isSourceCategoryAccepted(
  acceptedSources: string[] | undefined,
  sourceSchema: UniversalNodeSchema,
  sourceIsPassthrough: boolean,
): boolean {
  if (!acceptedSources || acceptedSources.includes('*')) return true
  if (sourceIsPassthrough) return true
  const aliases = sourceCategoryAliases[sourceSchema.category] || []
  return acceptedSources.some(
    (accepted) =>
      accepted === sourceSchema.type || // Direct node type match
      accepted === sourceSchema.category || // Category match
      aliases.includes(accepted), // Alias match
  )
}

/**
 * Validates if a source node can connect to a target node using Universal Node Schema
 */
export function validateNodeConnection(
  sourceNodeType: string,
  targetNodeType: string,
  targetHandle?: string,
  sourceHandle?: string,
  existingConnections?: { sourceType: string; targetHandle: string }[],
  targetNodeData?: Record<string, unknown>,
): ConnectionValidationResult {
  const sourceSchema = UniversalNodeRegistry.getSchema(sourceNodeType)
  const targetSchema = UniversalNodeRegistry.getSchema(targetNodeType)

  if (!sourceSchema || !targetSchema) {
    return {
      isValid: false,
      errorMessage: `Unknown node type: ${!sourceSchema ? sourceNodeType : targetNodeType}`,
    }
  }

  // Get source output type
  let sourceOutputType: string | undefined
  let sourceOutputPassthrough = false
  if (sourceHandle && sourceSchema.outputs) {
    const sourceOutput = sourceSchema.outputs.find((o) => o.id === sourceHandle)
    sourceOutputType = sourceOutput?.type
    sourceOutputPassthrough = sourceOutput?.passthrough ?? false
  } else if (sourceSchema.outputs && sourceSchema.outputs.length > 0) {
    // Default to first output type if no handle specified
    sourceOutputType = sourceSchema.outputs[0].type
    sourceOutputPassthrough = sourceSchema.outputs[0].passthrough ?? false
  }

  if (!sourceOutputType) {
    return {
      isValid: false,
      errorMessage: `${sourceSchema.displayName} has no outputs`,
    }
  }

  // For passthrough outputs (e.g. Bar Offset), the output type mirrors the
  // connected input's accepted types. Collect all possible types so downstream
  // nodes that accept any of them (e.g. AND/OR gate expecting boolean) can connect.
  let sourceOutputTypes: string[] = [sourceOutputType]
  if (sourceOutputPassthrough && sourceSchema.inputs.length > 0) {
    const inputAccepted = sourceSchema.inputs[0].acceptedTypes
    if (inputAccepted && inputAccepted.length > 0) {
      sourceOutputTypes = inputAccepted
    }
  }

  // Get target input requirements
  if (targetHandle) {
    // Check for multi-input nodes with dynamic inputs FIRST
    if (targetSchema.inputHandler?.type === 'multi' && targetSchema.inputs.length === 0) {
      // This is a fully dynamic multi-input node like merge
      const dynamicInputPattern = /^input_\d+$/
      if (dynamicInputPattern.test(targetHandle)) {
        // Create a virtual input definition based on the node category.
        // Debug sinks (debugValues) are pure observers — accept ANY
        // value-producing node, boolean or numeric (same philosophy as
        // debugChart's explicit input: a category allowlist goes stale).
        const virtualInput =
          targetSchema.category === 'debug'
            ? {
                id: targetHandle,
                displayName: targetHandle,
                acceptedTypes: ['number', 'boolean', 'ohlcv'],
                acceptedSources: ['*'],
              }
            : {
                id: targetHandle,
                displayName: targetHandle,
                acceptedTypes:
                  targetSchema.category === 'logic' ? ['boolean'] : ['number', 'ohlcv'],
                acceptedSources:
                  targetSchema.category === 'logic'
                    ? ['logic', 'filters', 'utilities', 'math', 'indicators']
                    : ['price-data', 'indicators', 'math', 'utilities'],
              }

        // Check accepted sources (passthrough sources are category-transparent)
        if (
          !isSourceCategoryAccepted(
            virtualInput.acceptedSources,
            sourceSchema,
            sourceOutputPassthrough,
          )
        ) {
          return {
            isValid: false,
            errorMessage: `${targetSchema.displayName} input '${virtualInput.displayName}' does not accept ${sourceSchema.type} nodes. Accepts: ${virtualInput.acceptedSources.join(', ')}`,
          }
        }

        // Check type compatibility (use all possible types for passthrough nodes)
        const acceptedTypes = virtualInput.acceptedTypes || []
        if (!isAnyTypeCompatible(sourceOutputTypes, acceptedTypes)) {
          return {
            isValid: false,
            errorMessage: `Type mismatch: ${sourceSchema.displayName} provides ${sourceOutputType} but ${targetSchema.displayName} requires ${acceptedTypes.join(' or ')}`,
          }
        }

        // If we get here, the connection is valid
        return { isValid: true }
      } else {
        return {
          isValid: false,
          errorMessage: `${targetSchema.displayName} has no input '${targetHandle}'`,
        }
      }
    }

    // Then check if the target schema has predefined inputs (non-empty array)
    if (targetSchema.inputs && targetSchema.inputs.length > 0) {
      const targetInput = targetSchema.inputs.find((input) => input.id === targetHandle)

      if (targetInput) {
        // Found predefined input, use it for validation
        // Check node type filter if present
        if (targetInput.nodeTypeFilter && !targetInput.nodeTypeFilter.includes(sourceNodeType)) {
          return {
            isValid: false,
            errorMessage: `${targetSchema.displayName} input '${targetInput.displayName}' only accepts: ${targetInput.nodeTypeFilter.map((type) => UniversalNodeRegistry.getSchema(type)?.displayName || type).join(', ')}`,
          }
        }

        // Check accepted sources (passthrough sources are category-transparent)
        if (
          !isSourceCategoryAccepted(
            targetInput.acceptedSources,
            sourceSchema,
            sourceOutputPassthrough,
          )
        ) {
          return {
            isValid: false,
            errorMessage: `${targetSchema.displayName} input '${targetInput.displayName}' does not accept ${sourceSchema.type} nodes. Accepts: ${targetInput.acceptedSources?.join(', ')}`,
          }
        }

        // Check type compatibility
        let acceptedTypes = targetInput.acceptedTypes || []
        const inputPicksField = !!targetInput.fieldSelection

        // Special handling for Target nodes based on targetType parameter
        if (targetSchema.type === 'target' && targetNodeData) {
          const targetType = getParameterValue(targetNodeData.parameters, 'targetType', '% Trade')

          // SIMPLE RULE: Condition = boolean only, everything else = number only
          if (targetType === 'Condition') {
            acceptedTypes = ['boolean']
          } else {
            acceptedTypes = ['number']
          }
        }

        // Entry Rules' signal means two things (audit item 10): a Limit
        // order's price, and on a Market order the entry trigger, which is how
        // the cookbook and the AI's example build an entry (entry-window.ts
        // reads a Market entry's signal as the trigger). A Market entry also
        // takes a price, which the Trade Manager lowering wires in.
        if (targetSchema.type === 'entryRules' && targetHandle === 'signal') {
          const entryType = getParameterValue(targetNodeData?.parameters, 'entryType', 'Market')
          if (entryType !== 'Limit') acceptedTypes = [...acceptedTypes, 'boolean']
        }

        if (!isAnyTypeCompatible(sourceOutputTypes, acceptedTypes, inputPicksField)) {
          return {
            isValid: false,
            errorMessage: `Type mismatch: ${sourceSchema.displayName} provides ${sourceOutputType} but ${targetSchema.displayName} requires ${acceptedTypes.join(' or ')}`,
          }
        }
      } else {
        return {
          isValid: false,
          errorMessage: `${targetSchema.displayName} has no input '${targetHandle}'`,
        }
      }
    }
  }

  // Special validation for Switcher nodes
  if (targetNodeType === 'switcher' && existingConnections && existingConnections.length > 0) {
    const switcherValidation = validateSwitcherTypeConsistency(
      sourceNodeType,
      sourceOutputType,
      existingConnections,
    )
    if (!switcherValidation.isValid) {
      return switcherValidation
    }
  }

  return { isValid: true }
}

/**
 * Check if source type is compatible with target accepted types
 */
function isTypeCompatible(
  sourceType: string,
  acceptedTypes: string[],
  inputPicksField: boolean,
): boolean {
  // Direct match
  if (acceptedTypes.includes(sourceType)) {
    return true
  }

  // A raw bar into a number input needs the input to pick a field (close,
  // high, ...); one that cannot (Position Sizing's signal) refuses it.
  if (sourceType === 'ohlcv' && !inputPicksField) return false

  // Check compatibility matrix
  const compatibleTypes = connectionCompatibilityMatrix[sourceType] || [sourceType]
  return acceptedTypes.some(
    (acceptedType) =>
      compatibleTypes.includes(acceptedType) ||
      (connectionCompatibilityMatrix[acceptedType] || []).includes(sourceType),
  )
}

/**
 * Check if any of the possible source types is compatible with the target accepted types.
 * Used for passthrough nodes that can output multiple types depending on their input.
 */
function isAnyTypeCompatible(
  sourceTypes: string[],
  acceptedTypes: string[],
  inputPicksField = true,
): boolean {
  return sourceTypes.some((t) => isTypeCompatible(t, acceptedTypes, inputPicksField))
}

/**
 * Validates type consistency for Switcher nodes
 */
function validateSwitcherTypeConsistency(
  _newSourceNodeType: string,
  newSourceType: string,
  existingConnections: { sourceType: string; targetHandle: string }[],
): ConnectionValidationResult {
  const getTypeCategory = (type: string): 'boolean' | 'numeric' | 'ohlcv' => {
    if (type === 'boolean') return 'boolean'
    if (type === 'ohlcv') return 'ohlcv'
    return 'numeric' // number, float, int, etc.
  }

  const newTypeCategory = getTypeCategory(newSourceType)

  // Check all existing connections for type compatibility
  for (const connection of existingConnections) {
    const existingSchema = UniversalNodeRegistry.getSchema(connection.sourceType)
    if (!existingSchema || !existingSchema.outputs) continue

    const existingOutputType = existingSchema.outputs[0]?.type
    if (!existingOutputType) continue

    const existingTypeCategory = getTypeCategory(existingOutputType)

    // Define compatibility rules
    const isCompatible = (() => {
      // Same type category is always compatible
      if (newTypeCategory === existingTypeCategory) return true

      // Numeric and OHLCV can mix (since field selection is available)
      if (
        (newTypeCategory === 'numeric' && existingTypeCategory === 'ohlcv') ||
        (newTypeCategory === 'ohlcv' && existingTypeCategory === 'numeric')
      ) {
        return true
      }

      // Boolean cannot mix with numeric or OHLCV
      return false
    })()

    if (!isCompatible) {
      const newTypeName = newTypeCategory === 'ohlcv' ? 'OHLCV' : newTypeCategory
      const existingTypeName = existingTypeCategory === 'ohlcv' ? 'OHLCV' : existingTypeCategory

      return {
        isValid: false,
        errorMessage: `Switcher inputs must have compatible types. Cannot mix ${newTypeName} with ${existingTypeName}.`,
      }
    }
  }

  return { isValid: true }
}

/**
 * Shared helper to determine if a node input at a given index is required.
 * Used by both UniversalNode.tsx for rendering "No input" chips and
 * by validateGraphBeforeBacktest for pre-run validation.
 *
 * @param schema - The node's UniversalNodeSchema
 * @param inputIndex - The index of the input to check
 * @returns boolean - Whether this input is required to have a connection
 */
/**
 * An input's position in the SCHEMA, which is what requiredness is keyed on.
 * A node face that shows a filtered list (a Market Entry Rules shows only its
 * Window) must not pass its row number. Dynamic inputs (a merge's input_N) are
 * not in the schema, so their row number is their position.
 */
export function schemaInputIndex(
  schema: UniversalNodeSchema,
  inputId: string,
  rowIndex: number,
): number {
  const index = schema.inputs.findIndex((input) => input.id === inputId)
  return index >= 0 ? index : rowIndex
}

export function isInputRequired(schema: UniversalNodeSchema, inputIndex: number): boolean {
  // An optional input (the entry window) is a choice, whatever the handler says.
  if (schema.inputs[inputIndex]?.optional) return false

  const inputHandler = schema.inputHandler

  // If has inputHandler, use its logic
  if (inputHandler) {
    switch (inputHandler.type) {
      case 'single':
        return inputIndex === 0 // First input is always required
      case 'dual':
        return inputIndex <= 1 // Both inputs (0 and 1) are always required
      case 'all':
        return inputIndex < schema.inputs.length
      case 'multi': {
        const minInputs = inputHandler.minInputs || 2
        return inputIndex < minInputs // First minInputs are required, rest optional
      }
      default:
        return false
    }
  }

  // No inputHandler means no requirements from the handler
  return false
}

/**
 * Determines if a node input should show the "No input" warning in the UI.
 * This is the authoritative source of truth for both rendering and validation.
 *
 * The logic exactly mirrors what UniversalNode.tsx does:
 * 1. If connected or in static mode: no warning
 * 2. If isInputRequired returns true: show warning
 * 3. If filter/indicator category: first input always shows warning when unconnected
 *
 * @param schema - The node's UniversalNodeSchema
 * @param inputIndex - The index of the input to check
 * @param isConnected - Whether this input has a connection
 * @param isStaticMode - Whether this input is in static mode (discriminated union set to static)
 * @returns boolean - Whether to show the "No input" warning
 */
export function shouldShowNoInputWarning(
  schema: UniversalNodeSchema,
  inputIndex: number,
  isConnected: boolean,
  isStaticMode: boolean = false,
): boolean {
  // If connected or in static mode, no warning needed
  if (isConnected || isStaticMode) {
    return false
  }

  // Check if input is required by inputHandler
  if (isInputRequired(schema, inputIndex)) {
    return true
  }

  // Filter and indicator nodes always require their first input to show warning
  // This matches the condition: (isInputRequired(index) || schema.category === 'filter' || schema.category === 'indicators')
  if ((schema.category === 'filter' || schema.category === 'indicators') && inputIndex === 0) {
    return true
  }

  return false
}
